import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Landing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Fade in animation on load
    setTimeout(() => {
      const fadeInElement = document.querySelector('.fade-in');
      if (fadeInElement) {
        fadeInElement.classList.add('visible');
      }
    }, 100);

    // Initialize particle system
    const initializeParticles = () => {
      const particleContainer = document.getElementById('particles');
      if (!particleContainer || !window.p5) return;

      new window.p5((p) => {
        let particles = [];
        
        p.setup = () => {
          const canvas = p.createCanvas(p.windowWidth, p.windowHeight);
          canvas.parent('particles');
          
          // Create particles (bigger size)
          for (let i = 0; i < 50; i++) {
            particles.push({
              x: p.random(p.width),
              y: p.random(p.height),
              vx: p.random(-0.5, 0.5),
              vy: p.random(-0.5, 0.5),
              size: p.random(4, 10),
              opacity: p.random(0.1, 0.3)
            });
          }
        };
        
        p.draw = () => {
          p.clear();
          
          // Update and draw particles
          particles.forEach(particle => {
            particle.x += particle.vx;
            particle.y += particle.vy;
            
            // Wrap around edges
            if (particle.x < 0) particle.x = p.width;
            if (particle.x > p.width) particle.x = 0;
            if (particle.y < 0) particle.y = p.height;
            if (particle.y > p.height) particle.y = 0;
            
            // Draw particle
            p.fill(59, 130, 246, particle.opacity * 255);
            p.noStroke();
            p.circle(particle.x, particle.y, particle.size);
          });
        };
        
        p.windowResized = () => {
          p.resizeCanvas(p.windowWidth, p.windowHeight);
        };
      });
    };

    // Initialize particles after a short delay
    const timer = setTimeout(() => {
      initializeParticles();
      
      // Add smooth entrance animation with anime.js
      if (window.anime) {
        window.anime({
          targets: '.fade-in',
          opacity: [0, 1],
          translateY: [30, 0],
          duration: 500,
          easing: 'easeOutCubic',
          delay: 100
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      // Clean up p5 canvas
      const canvas = document.querySelector('#particles canvas');
      if (canvas) {
        canvas.remove();
      }
    };
  }, []);

  const goToDashboard = () => {
    sessionStorage.setItem('fromLanding', 'true');
    navigate('/dashboard');
  };

  return (
    <div className="bg-gray-50 text-gray-900">
      {/* Main Content */}
      <main className="min-h-screen flex items-center justify-center hero-bg">
        <div className="particle-container" id="particles"></div>
        <div className="content-overlay w-full">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="fade-in">
              <h1 className="text-6xl md:text-8xl font-bold text-gray-900 mb-6">
                <span className="text-gradient">Exam Prasna</span>
              </h1>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Old Questions Pattern Analysis
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Advanced analytics of past TU exam questions from faculties like BCT, BEL, BEX, BME to help you prepare effectively.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <button 
                  onClick={goToDashboard} 
                  className="btn-primary text-white px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center justify-center"
                >
                  Enter Dashboard
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                  </svg>
                </button>
                <Link 
                  to="/courses" 
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors"
                >
                  Explore Courses
                </Link>
              </div>

              {/* Features Preview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto">
                    <div className="text-4xl mb-4">📊</div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Analytics</h3>
                  <p className="text-gray-600 text-sm">Interactive Chart, Chapter-wise Analysis for Deep Insights</p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto">
                    <div className="text-4xl mb-4">📚</div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Organized</h3>
                  <p className="text-gray-600 text-sm">Questions sorted by Chapters, Tags, and Master Type</p>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto">
                    <div className="text-4xl mb-4">🔍</div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Filtering</h3>
                  <p className="text-gray-600 text-sm">Multi-criteria Filters:<br />Marks, Year, Set, etc</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for Excellence</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              A professional question analysis platform<br /> designed by educators for serious students
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8 fade-in">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  Before Learning, we must know: what are important topics we need to prioritize.<br /><br />
                  Effective learning comes from deep understanding of question patterns. Our platform transforms raw question data into 
                  insights that help students make informed decisions.
                </p>
                <p className="text-gray-600 text-lg leading-relaxed">
                  When you're preparing for exams, creating study materials, or analyzing 
                  manually.<br />Exam Prasna Platform helps you unlock the precision you need 
                  to maximize your efficiency on learning for Exams.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-2">1000+</div>
                  <div className="text-gray-600">Questions Processed</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="text-3xl font-bold text-green-600 mb-2">500+</div>
                  <div className="text-gray-600">Active Users</div>
                </div>
              </div>
            </div>
            <div className="space-y-6 fade-in">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Benefits</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-600">Filter High Priority Questions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-600">Data-driven Question Patterns</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-600">Interactive Charts with Filters</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-600">Organized by Chapters</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-600">Master Type and Variations</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
                <h4 className="text-lg font-semibold mb-4">Ready to Start?</h4>
                <p className="text-blue-100 mb-4">
                  Join thousands of students who trust our platform 
                  for their question analysis needs.
                </p>
                <Link
                  to="/register"
                  className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-block"
                >
                  Create Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gradient">Exam Prasna</h3>
              <p className="text-gray-400 mb-4">Professional learning platform with advanced analytics and build for Excellence</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Courses</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/courses" className="hover:text-white transition-colors">Computer Networks</Link></li>
                <li><Link to="/courses" className="hover:text-white transition-colors">Machine Learning</Link></li>
                <li><Link to="/courses" className="hover:text-white transition-colors">Web Development</Link></li>
                <li><Link to="/courses" className="hover:text-white transition-colors">Cybersecurity</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Exam Prasna. All rights reserved. Built with passion for learning.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
